import * as React from 'react';

import { illustrations } from './feedback-button.mapping';
import * as Styled from './feedback-button.styles';
import type {
  FeedbackButtonProps,
  FeedbackButtonVisualState,
} from './feedback-button.types';

export const FeedbackButton = React.forwardRef<
  HTMLButtonElement,
  FeedbackButtonProps
>(
  (
    { children, className, disabled, expression, onClick, variant, ...props },
    forwardedRef
  ) => {
    const [hover, setHover] = React.useState(false);
    const [active, setActive] = React.useState(false);
    const [focusVisible, setFocusVisible] = React.useState(false);

    // Keep focus-visible logic keyboard-friendly
    const pointerMod = React.useRef(false);

    const onPointerDown = (): void => {
      pointerMod.current = true;
      setActive(true);
    };

    const onPointerUp = (): void => {
      pointerMod.current = false;
      setActive(false);
    };

    const onMouseEnter = (): void => {
      setHover(true);
    };

    const onMouseLeave = (): void => {
      setHover(false);
      setActive(false);
    };

    const onFocus = (): void => {
      setFocusVisible(!pointerMod.current);
      pointerMod.current = false;
    };

    const onBlur = (): void => {
      setFocusVisible(false);
      pointerMod.current = false;
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === ' ' || event.key === 'Enter') setActive(true);
    };

    const onKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === ' ' || event.key === 'Enter') setActive(false);
    };

    const getVisualState = (): FeedbackButtonVisualState => {
      switch (true) {
        case disabled:
          return 'disabled';
        case active:
          return 'active';
        case focusVisible:
          return 'focus';
        case hover:
          return 'hover';
        default:
          return 'default';
      }
    };

    const Illustration = illustrations[variant][expression][getVisualState()];

    return (
      <Styled.FeedbackButton
        $isDisabled={disabled}
        aria-pressed={active || undefined}
        className={className}
        data-xds="FeedbackButton"
        disabled={disabled}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        ref={forwardedRef}
        type="button"
        {...props}
      >
        <Styled.IconWrapper aria-hidden="true">
          <Illustration />
        </Styled.IconWrapper>
        <Styled.Label as="div" $isActive={active} $isHovered={hover}>
          {children}
        </Styled.Label>
      </Styled.FeedbackButton>
    );
  }
);

FeedbackButton.displayName = 'FeedbackButton';
